import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { API } from "../lib/api";
import Layout from "../components/Layout";
import AuthImage from "../components/AuthImage";
import AuthVideo from "../components/AuthVideo";
import { useAuth } from "../contexts/AuthContext";
import { Heart, Trophy, Lock, X, Check, XCircle, Sparkles } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABELS = {
  image:   "Månedens bilde",
  video:   "Månedens film",
  novelle: "Månedens novelle",
  creator: "Månedens skaper",
};

function EntryCard({ entry, contest, canVote, onToggle, isMine, isAdmin, onApprove, onReject, isGuest, voteRequiresVip }) {
  const { t } = useTranslation();
  const media = entry.media;
  const isImage = contest.type === "image" && media;
  const isVideo = contest.type === "video" && media;
  const isNovelle = contest.type === "novelle";
  const owner = entry.owner || {};
  const isPending = entry.approval_status === "pending";
  // VIP-gate på video: vis blur + lås om bruker ikke har VIP (samme oppførsel som /bilder)
  const videoVipLocked = isVideo && voteRequiresVip;
  // Bilder/videoer serveres via signerte URL-er (AuthImage/AuthVideo håndterer signering).
  // Vi bruker entry.ref_id som media-id; hvis den mangler, faller vi tilbake til media.url.
  const mediaRefId = entry.ref_id || (media && media.id);
  const legacyMediaSrc = (media && media.url) || null;
  return (
    <div
      data-testid={`entry-${entry.id}`}
      className={`panel-card flex flex-col overflow-hidden transition ${entry.my_vote ? "ring-2 ring-[var(--magenta)]" : ""} ${isPending ? "ring-1 ring-[var(--gold)]/60" : ""}`}
    >
      {/* Pending-banner */}
      {isPending && (
        <div className="bg-[var(--gold)]/15 border-b border-[var(--gold)]/40 px-3 py-1.5 font-meta text-[10px] text-[var(--gold)] uppercase tracking-wider">
          Venter på godkjenning
        </div>
      )}
      {/* Media */}
      <div className="aspect-[3/4] bg-[var(--noir-elev)] relative overflow-hidden">
        {isImage && (mediaRefId || legacyMediaSrc) && (
          legacyMediaSrc ? (
            <img
              src={legacyMediaSrc}
              alt=""
              className={`absolute inset-0 w-full h-full object-cover ${isGuest ? "blur-2xl scale-110" : ""}`}
              data-testid={`entry-media-${entry.id}`}
            />
          ) : (
            <AuthImage
              imageId={mediaRefId}
              alt=""
              className={`absolute inset-0 w-full h-full object-cover ${isGuest ? "blur-2xl scale-110" : ""}`}
              data-testid={`entry-media-${entry.id}`}
            />
          )
        )}
        {isVideo && mediaRefId && !videoVipLocked && (
          <AuthVideo
            videoId={mediaRefId}
            controls
            playsInline
            preload="none"
            className={`absolute inset-0 w-full h-full object-cover bg-black ${isGuest ? "blur-2xl scale-110 pointer-events-none" : ""}`}
            data-testid={`entry-media-${entry.id}`}
          />
        )}
        {isVideo && videoVipLocked && (
          <>
            {/* Blurred poster (bruker /images/{ref_id}/raw som video-thumbnail) */}
            {mediaRefId && (
              <AuthImage
                imageId={mediaRefId}
                alt=""
                className="absolute inset-0 w-full h-full object-cover blur-2xl scale-110"
              />
            )}
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/70 backdrop-blur-sm gap-2 z-10" data-testid={`entry-vip-gate-${entry.id}`}>
              <span className="px-2 py-0.5 bg-[var(--gold)] text-[var(--noir)] text-[10px] font-bold uppercase tracking-wider">VIP</span>
              <span className="font-fraunces italic text-sm text-[var(--cream)] text-center px-3">Video-konkurranser krever VIP</span>
              <a href="/vip" className="mt-1 px-3 py-1 text-[10px] font-meta uppercase tracking-[0.15em] bg-[var(--magenta)] text-black hover:opacity-90">Bli VIP</a>
            </div>
          </>
        )}
        {isGuest && (isImage || isVideo) && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40 p-3 z-10">
            <div className="text-center">
              <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--magenta)] mb-2">LÅST · 18+</div>
              <a href="/?tab=register" className="btn-magenta text-xs px-3 py-1.5" data-testid="entry-locked-cta">Bli med gratis</a>
            </div>
          </div>
        )}
        {isNovelle && (
          <div className="absolute inset-0 p-5 flex flex-col">
            <div className="font-meta text-[var(--gold)] mb-2">NOVELLE</div>
            <h4 className="font-fraunces italic text-2xl text-[var(--cream)] mb-3">{media?.title || "Uten tittel"}</h4>
            <p className="text-sm text-[var(--cream-soft)] line-clamp-6">{media?.teaser}</p>
          </div>
        )}
        {!media && contest.type === "creator" && (
          <div className="absolute inset-0 flex items-center justify-center">
            {owner.avatar_url
              ? <img src={owner.avatar_url} alt="" className="w-full h-full object-cover" />
              : <div className="text-[var(--cream-faint)] font-fraunces text-5xl italic">{(owner.username || "?").slice(0,2).toUpperCase()}</div>}
          </div>
        )}
        {/* Votes overlay */}
        <div className="absolute bottom-2 right-2 bg-black/70 backdrop-blur-sm px-2 py-1 text-xs font-meta">
          <Heart className="h-3 w-3 inline mr-1" /> {entry.votes_count}
        </div>
      </div>

      {/* Footer */}
      <div className="p-3 flex items-center justify-between gap-2">
        <Link to={`/profile/${owner.username}`} className="flex items-center gap-2 min-w-0">
          {owner.avatar_url && <img src={owner.avatar_url} alt="" className="w-7 h-7 object-cover" />}
          <span className="text-sm text-[var(--cream)] truncate">{owner.username || "?"}</span>
        </Link>
        {isAdmin && isPending ? (
          <div className="flex gap-1">
            <button
              onClick={() => onApprove(entry.id)}
              data-testid={`approve-${entry.id}`}
              className="px-2 py-1 text-xs font-meta border border-[var(--sage)]/60 text-[var(--sage)] hover:bg-[var(--sage)]/15 transition"
              title="Godkjenn"
            >
              <Check className="h-3 w-3" />
            </button>
            <button
              onClick={() => onReject(entry.id)}
              data-testid={`reject-${entry.id}`}
              className="px-2 py-1 text-xs font-meta border border-[var(--magenta)]/60 text-[var(--magenta)] hover:bg-[var(--magenta)]/15 transition"
              title="Avvis"
            >
              <XCircle className="h-3 w-3" />
            </button>
          </div>
        ) : isMine ? (
          <span className="font-meta text-xs text-[var(--gold)]">DITT BIDRAG</span>
        ) : videoVipLocked ? (
          <a
            href="/vip"
            data-testid={`vote-vip-locked-${entry.id}`}
            title="VIP kreves for å stemme på video-konkurranser"
            className="px-3 py-1.5 text-xs font-meta border border-[var(--gold)]/60 text-[var(--gold)] hover:bg-[var(--gold)]/15"
          >
            <Lock className="h-3 w-3 inline mr-1" /> VIP
          </a>
        ) : canVote && !isPending ? (
          <button
            onClick={() => !entry.my_vote && onToggle(entry.id)}
            disabled={!!entry.my_vote}
            data-testid={`vote-${entry.id}`}
            title={entry.my_vote ? "Du har stemt på dette bidraget. Stemmer kan ikke trekkes tilbake." : "Avgi stemme (kan ikke angres)"}
            className={`px-3 py-1.5 text-xs font-meta border transition ${entry.my_vote ? "bg-[var(--magenta)] text-black border-[var(--magenta)] cursor-not-allowed" : "border-white/20 text-[var(--cream)] hover:border-[var(--magenta)]"}`}
          >
            <Heart className="h-3 w-3 inline mr-1" fill={entry.my_vote ? "currentColor" : "none"} />
            {entry.my_vote ? "Stemt" : "Stem"}
          </button>
        ) : (
          <span className="text-xs text-[var(--cream-faint)]"><Lock className="h-3 w-3 inline" /></span>
        )}
      </div>
    </div>
  );
}

export default function KonkurranseView() {
  const { id } = useParams();
  const { user } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [spinStatus, setSpinStatus] = useState(null); // { has_grant, spun }

  const load = () => {
    setLoading(true);
    api.get(`/contests/${id}`).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  };
  const loadSpinStatus = () => {
    if (!user) { setSpinStatus(null); return; }
    api.get(`/me/contests/${id}/spin-status`).then((r) => setSpinStatus(r.data)).catch(() => setSpinStatus(null));
  };
  useEffect(() => { load(); loadSpinStatus(); /* eslint-disable-next-line */ }, [id, user?.id]);

  if (loading) return <Layout><div className="text-center text-[var(--cream-soft)] py-20">Laster…</div></Layout>;
  if (!data) return <Layout><div className="text-center text-[var(--cream-soft)] py-20">Ikke funnet.</div></Layout>;

  const c = data.contest;
  const entries = data.entries || [];
  const winners = c.winners || [];
  const canEnter = c.phase === "upload" && user && !data.my_entry_id;
  const canVote = ["upload", "voting"].includes(c.phase) && !!user;
  const isAdmin = user?.role === "admin" || user?.role === "owner";
  const pendingCount = entries.filter((e) => e.approval_status === "pending").length;

  const toggleVote = async (entryId) => {
    try {
      const res = await api.post(`/contests/${id}/entries/${entryId}/vote`);
      load();
      // Hvis dette spinnet låste opp et lykkehjul-spinn → vis tydelig melding + oppdater status
      if (res.data?.spin_granted) {
        toast.success("🎰 Du har låst opp et spinn på avstemnings-hjulet!");
        loadSpinStatus();
      } else {
        loadSpinStatus();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunne ikke stemme");
    }
  };

  const approveEntry = async (entryId) => {
    try {
      await api.post(`/admin/contests/${id}/entries/${entryId}/approve`);
      toast.success("Bidrag godkjent");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunne ikke godkjenne");
    }
  };

  const rejectEntry = async (entryId) => {
    const reason = window.prompt("Begrunnelse for avvisning (sendes til bruker):", "Bidraget oppfyller ikke konkurransevilkårene.");
    if (reason === null) return;
    try {
      await api.post(`/admin/contests/${id}/entries/${entryId}/reject`, null, { params: { reason } });
      toast.success("Bidrag avvist");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunne ikke avvise");
    }
  };

  const removeMine = async () => {
    if (!window.confirm("Trekke ditt bidrag?")) return;
    await api.delete(`/contests/${id}/my-entry`);
    load();
  };

  return (
    <Layout>
      {/* Hero */}
      <div className="panel mb-8 p-8 relative overflow-hidden">
        {c.banner_url && (
          <>
            <img src={c.banner_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
            <div className="absolute inset-0 bg-gradient-to-r from-[var(--noir)] via-transparent to-[var(--noir)]" />
          </>
        )}
        <div className="relative">
          <div className="font-meta text-[var(--gold)] mb-2">{TYPE_LABELS[c.type] || c.type.toUpperCase()}</div>
          <h1 className="font-fraunces italic text-4xl md:text-5xl text-[var(--cream)] mb-3">{c.title}</h1>
          {c.description && <p className="text-[var(--cream-soft)] mb-4 max-w-2xl">{c.description}</p>}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-[var(--cream-faint)] font-meta">
            <span>{entries.length} BIDRAG</span>
            {c.prize_amount > 0 && <span className="text-[var(--gold)]">PREMIE: {c.prize_amount.toLocaleString("nb-NO")} CHIPS</span>}
            <span>SLUTT: {new Date(c.ends_at).toLocaleDateString("nb-NO")}</span>
            {canVote && (
              <span className="text-[var(--magenta)]">
                MINE STEMMER: {data.my_votes_count} / {data.max_votes}
              </span>
            )}
          </div>
          <div className="mt-6 flex gap-3 flex-wrap">
            {canEnter && (
              <Link
                to={`/konkurranser/${id}/bidrag`}
                data-testid="enter-contest-btn"
                className="px-5 py-2.5 bg-[var(--magenta)] text-black font-meta"
              >
                Meld inn bidrag
              </Link>
            )}
            {data.my_entry_id && c.phase === "upload" && (
              <button onClick={removeMine} className="px-5 py-2.5 border border-white/20 text-[var(--cream)] font-meta">
                <X className="h-3.5 w-3.5 inline mr-2" /> Trekk mitt bidrag
              </button>
            )}
          </div>
          {canVote && (
            <p className="mt-4 text-[var(--cream-faint)] text-xs font-meta" data-testid="vote-rule-info">
              {t("contests.vote_rule_local", { n: data.max_votes }).toUpperCase()}
            </p>
          )}
        </div>
      </div>

      {/* Vinnere */}
      {winners.length > 0 && (
        <div className="mb-10">
          <div className="font-meta text-[var(--gold)] mb-3 flex items-center gap-2"><Trophy className="h-3.5 w-3.5" /> VINNERE</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {winners.map((w) => {
              const ent = entries.find((e) => e.id === w.entry_id);
              return (
                <div key={w.entry_id} className="panel-card p-4 border-l-4" style={{ borderLeftColor: w.place === 1 ? "var(--gold)" : w.place === 2 ? "var(--cream-soft)" : "var(--copper)" }}>
                  <div className="font-meta text-[var(--gold)]">{w.place}. PLASS</div>
                  <div className="text-[var(--cream)] font-medium mt-1">{ent?.owner?.username || "?"}</div>
                  {w.tokens_awarded > 0 && <div className="text-[var(--cream-soft)] text-sm mt-1">+{w.tokens_awarded} chips</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Spinn lykkehjulet - vises rett over bidragsgriden saa den ikke overses */}
      {spinStatus?.has_grant && !spinStatus?.spun && (
        <Link
          to={`/avstemnings-hjul/${id}`}
          data-testid="spin-wheel-banner"
          className="block mb-6 p-4 bg-[var(--magenta)]/10 border border-[var(--magenta)]/40 hover:bg-[var(--magenta)]/15 transition-colors"
        >
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Sparkles className="h-5 w-5 text-[var(--magenta)]" />
              <div>
                <div className="font-fraunces italic text-lg text-[var(--cream)]">Du har låst opp et spinn!</div>
                <div className="font-meta text-xs tracking-[0.15em] text-[var(--cream-soft)] mt-0.5">SPINN LYKKEHJULET FOR Å VINNE PREMIER</div>
              </div>
            </div>
            <span className="font-meta text-xs tracking-[0.15em] text-[var(--magenta)] uppercase hidden sm:inline">Spinn nå →</span>
          </div>
        </Link>
      )}

      {/* Admin pending-banner */}
      {isAdmin && pendingCount > 0 && (
        <div className="bg-[var(--gold)]/10 border border-[var(--gold)]/40 px-5 py-3 mb-4 font-meta text-sm text-[var(--gold)]" data-testid="admin-pending-banner">
          <strong>{pendingCount}</strong> bidrag venter på din godkjenning. Bla nedover for å godkjenne/avvise direkte på hvert kort.
        </div>
      )}

      {/* VIP-gate-banner for ikke-VIP brukere på video-konkurranser */}
      {data.vote_requires_vip && user && (
        <div className="bg-[var(--gold)]/10 border border-[var(--gold)]/40 px-5 py-3 mb-4 font-meta text-sm text-[var(--cream)] flex items-center justify-between gap-3" data-testid="video-vote-vip-banner">
          <span>
            <Lock className="h-3.5 w-3.5 inline mr-2 text-[var(--gold)]" />
            Video-konkurranser krever VIP for å se bidragene og avgi stemmer.
          </span>
          <a href="/vip" className="px-3 py-1.5 text-xs font-meta uppercase tracking-[0.15em] bg-[var(--magenta)] text-black hover:opacity-90 shrink-0" data-testid="video-vote-vip-cta">Bli VIP</a>
        </div>
      )}

      {/* Entries */}
      {entries.length === 0 ? (
        <div className="panel-tight p-12 text-center text-[var(--cream-soft)]">
          Ingen bidrag ennå. {canEnter && "Bli den første!"}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {entries.map((e) => (
            <EntryCard
              key={e.id}
              entry={e}
              contest={c}
              canVote={canVote && e.user_id !== user?.id && !data.vote_requires_vip}
              isMine={e.user_id === user?.id}
              isAdmin={isAdmin}
              isGuest={!user}
              voteRequiresVip={!!data.vote_requires_vip}
              onToggle={toggleVote}
              onApprove={approveEntry}
              onReject={rejectEntry}
            />
          ))}
        </div>
      )}
    </Layout>
  );
}
